import React from 'react';
import { ProjectState, ToolExperienceState } from '../../definitions/Project';
import styles from './ProjectCard.module.scss';
import { useNavigate } from 'react-router-dom';
import { FILTERS } from '../../filters/filtersConfig';

interface ProjectCardProps {
    project: ProjectState;
    tokenAvailable: boolean;
    addProject?: () => Promise<void>;
    deleteProject?: () => Promise<void>;
    projectAdded?: boolean;
    loading?: boolean;
    myProject?: boolean;
    isMyProject?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ 
    project, 
    tokenAvailable, 
    addProject, 
    deleteProject, 
    projectAdded, 
    loading, 
    myProject = false,
    isMyProject = false
}) => {
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/projects/${project._id}`);
    };

    const formatDate = (timestamp: number | undefined) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };



    const renderStringArray = (items: string[] | undefined, maxVisible: number) => {
         if (!items || items.length === 0) {
            return <span>Not specified</span>;
        }

        // Jeśli jest więcej niż maxVisible elementów, spróbuj pokazać więcej w jednej linii
        if (items.length > maxVisible) {
            // Sprawdź czy możemy pokazać więcej elementów w jednej linii
            const extendedMax = Math.min(items.length, maxVisible + 2); // Dodaj 2 więcej
            const visibleItems = items.slice(0, extendedMax).join(', ');
            const remainingCount = items.length - extendedMax;
            
            if (remainingCount > 0) {
                return (
                    <span>
                        {visibleItems}
                        <span className={styles.moreTextContainer}>
                            <span className={styles.moreText}>, +{remainingCount} more</span>
                            <div className={styles.tooltip}>
                                {items.slice(extendedMax).join(', ')}
                            </div>
                        </span>
                    </span>
                );
            } else {
                return <span>{visibleItems}</span>;
            }
        }

        // Jeśli jest maxVisible lub mniej elementów, połącz je przecinkami
        return <span>{items.join(', ')}</span>;
    };

    if (loading) {
        return <div className={styles.card}>Loading...</div>;
    }

    return (
        <div className={styles.card} onClick={handleClick}>
            <div className={styles.header}>
                <h2 className={styles.title}>{project.projectName}</h2>
                <div className={styles.actions}>
                    {myProject && (
                        <button 
                            className={styles.editButton}
                            onClick={e => {
                                e.stopPropagation();
                                navigate(`/project/editProject/${project._id}`);
                            }}
                        >
                            Edit
                        </button>
                    )}
                    {isMyProject ? (
                        <div className={styles.myProjectBadge}>
                            <span>My Project</span>
                        </div>
                    ) : tokenAvailable && (
                        <>
                            {!projectAdded ? (
                                <button 
                                    className={styles.saveButton} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        addProject?.();
                                    }}
                                >
                                    Save
                                </button>
                            ) : (
                                <button 
                                    className={styles.forgetButton} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteProject?.();
                                    }}
                                >
                                    Forget
                                </button>
                            )}
                        </>
                    )}
                    <button 
                        className={styles.actionButton} 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClick();
                        }}
                    >
                        See more
                    </button>
                </div>
            </div>
            


            <div className={styles.detailsSection}>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.projectType.label}:</strong>
                    <span> {renderStringArray(project.projectType ? [project.projectType] : undefined, 2)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.productType ? FILTERS.productType.label : 'Product Type'}:</strong>
                    <span> {renderStringArray(project.productType, 3)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.offeredGratification.label}:</strong>
                    <span> {renderStringArray(project.offeredGratification, 3)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.forGratification.label}:</strong>
                    <span> {renderStringArray(project.forGratification, 3)}</span>
                </div>
                <div className={styles.detailsRow}>
                    <strong>{FILTERS.toolsExperience.label}:</strong>
                    <span> {renderStringArray(project.toolsExperience?.map(exp => exp.tool) || [], 4)}</span>
                </div>
            </div>

            <div className={styles.footer}>
                <span>{FILTERS.locations.label}: {
                    project.locations && project.locations.length > 0 
                        ? renderStringArray(project.locations, 2)
                        : (project.workingTypeRemote ? 'Remote' : 'Not specified')
                }</span>
                <span className={styles.moreText}>See more</span>
            </div>
        </div>
    );
};

export default ProjectCard; 